import * as Sentry from '@sentry/react';
import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Loader } from '@cashfree-intl/coherent';

// Utils
import { TEST } from 'utils/constants';
import { getRequestUrlAndToken } from 'utils/common';
import { syncSentryProductStatus } from 'utils/sentry/merchantTags';

import { AuthContext } from '../contexts/AuthContext';
import { MerchantContext } from '../contexts/MerchantContext';

import {
  getFeatureToggleConfig,
  fetchEasySplitConfig,
  fetchActivePaymentModes,
  // fetchOffersConfig,
  fetchRouterActivationStatus,
  fetchOnDemandEligibility,
  getProductStatus,
  fetchUnificationOnboarding,
  fetchWhitelisting,
  getInstantRefundsFreeTrialEligibility,
  getShopifyMerchant,
  fetchFlowwiseActivationStatus,
  getPgCaptureConfig,
  getProductPrivilege,
  getProductFruitCampaigns,
  getSSOConfig,
} from '../services/pgConfig';

// Need to export as components, apart from default for supporting Module Federation
export function MerchantProvider({ children }) {
  const dispatch = useDispatch();
  const [data, setData] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [selectedTimeZone, setSelectedTimeZone] = useState('IST');
  const authObj = useContext(AuthContext);
  const { env, userObj } = authObj || {};
  // get merchantToken or gammaToken or token (in case of partners).
  const { token } = getRequestUrlAndToken();

  useEffect(() => {
    (async function fetchData() {
      if (token || (userObj && userObj.cfToken && userObj.cfToken[env])) {
        const [
          { obj: featureToggles, response: featureTogglesWithVariant },
          {
            easySplitEnabled,
            easySplitStatus,
            criticalMerchant,
            isVpaEnabled,
            vendorDashboardEnabled,
            staticSplitEnabled,
          },
          { isUPIEnabled, isVbaEnabled },
          // offersEnabled,
          { showRouterTab, routerStatus },
          isOnDemandEligible,
          productStatus,
          { showInstantSettlementsAlertBar, unificationDate, showInstantTab },
          showBatchLinkUpload,
          instantRefundsFreeTrialEligibility,
          { isShopifyMerchant, shopifyStoreUrl },
          flowWiseActivation,
          captureConfig,
          productPrivilege,
          productFruitCampaigns,
          ssoFlag,
        ] = await Promise.all([
          getFeatureToggleConfig(),
          fetchEasySplitConfig(),
          fetchActivePaymentModes(),
          // fetchOffersConfig(),
          fetchRouterActivationStatus(),
          fetchOnDemandEligibility(),
          getProductStatus(),
          fetchUnificationOnboarding(),
          fetchWhitelisting(),
          getInstantRefundsFreeTrialEligibility(),
          getShopifyMerchant(),
          fetchFlowwiseActivationStatus(),
          getPgCaptureConfig(),
          getProductPrivilege(),
          getProductFruitCampaigns(),
          getSSOConfig(),
        ]);

        const isTest = env === TEST;
        /**
         * render Chatbot if:
         * 1. user is not in test-env
         */
        const renderChatbot = !isTest;

        // FP-5353: never put undefined on context — consumers do featureToggles[FLAG] in places
        // (e.g. federated / legacy bundles) and throw on merchants/landing.
        const rawFeatureToggles = featureToggles;
        const safeFeatureToggles = {
          ...(rawFeatureToggles ?? {}),
          ...(process.env.SHOW_V2
            ? { SHOW_PGDB_V2: true, SHOW_PGDB_V2_HOME_INSIGHTS: true }
            : {}),
        };
        const safeFeatureTogglesWithVariant = featureTogglesWithVariant ?? {};

        Sentry.setContext('featureToggles', safeFeatureToggles);
        if (rawFeatureToggles == null) {
          Sentry.captureMessage('[FeatureToggle] featureToggles undefined before setData', {
            level: 'warning',
            tags: { label: 'feature-toggle' },
            extra: { context: 'MerchantProvider — flags will be missing for this session' },
          });
        }
        setData({
          featureToggles: safeFeatureToggles,
          productStatus,
          isUPIEnabled,
          easySplitStatus,
          // offersEnabled,
          easySplitEnabled,
          isVpaEnabled,
          showRouterTab,
          routerStatus,
          isOnDemandEligible,
          criticalMerchant,
          showInstantSettlementsAlertBar,
          unificationDate,
          showInstantTab,
          showBatchLinkUpload,
          renderChatbot,
          vendorDashboardEnabled,
          staticSplitEnabled,
          ssoFlag,
          isVbaEnabled,
          instantRefundsFreeTrialEligibility,
          isShopifyMerchant,
          shopifyStoreUrl,
          isFlowwiseActive: flowWiseActivation,
          captureConfig,
          productPrivilege,
          productFruitCampaigns,
          featureTogglesWithVariant: safeFeatureTogglesWithVariant,
          isMerchantImportEnabled: ['APPROVED', 'UNDER_REVIEW'].includes(
            productStatus?.IMPC,
          ),
        });
        // Session-level Sentry enrichment: high-signal tags + full map context
        syncSentryProductStatus(productStatus);
        setLoading(false);
      }
    })();
  }, [env]);

  const resetFeatureToggles = useCallback(async () => {
    try {
      const productFruitCampaigns = await getProductFruitCampaigns(true);
      setData(prevData => ({
        ...prevData,
        productFruitCampaigns,
      }));
    } catch (error) {
      console.error('Failed to fetch productFruitCampaigns:', error);
    }
  }, []);

  const value = useMemo(() => {
    dispatch({
      type: 'SET_IS_SHELL_V2',
      payload: true,
    });

    const safePayload = {
      ...data,
      featureToggles: data.featureToggles ?? {},
      featureTogglesWithVariant: data.featureTogglesWithVariant ?? {},
    };

    dispatch({
      type: 'SET_FEATURE_FLAGS',
      payload: safePayload,
    });

    return {
      ...safePayload,
      setMerchantContextData: setData,
      resetFeatureToggles,
      selectedTimeZone,
      setSelectedTimeZone,
    };
  }, [data, selectedTimeZone]);
  return (
    <MerchantContext.Provider value={value}>
      {isLoading ? <Loader active={isLoading} /> : children}
    </MerchantContext.Provider>
  );
}

MerchantProvider.propTypes = {
  children: PropTypes.node,
};

export default MerchantProvider;